/**
 * Framework-agnostic pixel tracker.
 *
 * Init / fire logic for Meta, TikTok, Snapchat, Pinterest, GTM/GA4 pixels —
 * extracted from `react/hooks/usePixelTracking.tsx` so non-React entry points
 * (Studio islands hydrator, full-SPA runtime) can fire pixel events from
 * `window.__TGD_STEP_CONFIG__.pixels`.
 *
 * The React provider is a thin adapter on top of this module.
 */
import { resolvePixelEvents, } from './pixelMapping';
// ---------------------------------------------------------------------------
// Duplicate guard (time-window based)
// ---------------------------------------------------------------------------
function createDuplicateGuard(windowMs) {
    const lastEvents = new Map();
    return (eventName, parameters, eventId) => {
        try {
            // Include eventId in the dedup key so two distinct logical events with the
            // same name+params (e.g. two Purchases) are not collapsed by the guard.
            const key = JSON.stringify({ eventName, parameters, eventId });
            const now = Date.now();
            const last = lastEvents.get(key);
            if (last && now - last < windowMs)
                return false;
            lastEvents.set(key, now);
            return true;
        }
        catch {
            return true;
        }
    };
}
// ---------------------------------------------------------------------------
// Browser-specific: fire an event to the correct global pixel function
// ---------------------------------------------------------------------------
/* eslint-disable @typescript-eslint/no-explicit-any */
function fire(provider, name, params, pixel, options = {}) {
    if (typeof window === 'undefined')
        return;
    const w = window;
    const eventId = options.eventId;
    switch (provider) {
        case 'facebook':
            // Meta dedup: pass eventID as the 4th-arg `eventID` option so this
            // browser-pixel event matches a server-side CAPI event with the same id.
            // See https://developers.facebook.com/docs/marketing-api/conversions-api/deduplicate-pixel-and-server-events
            if (eventId)
                w.fbq?.('track', name, params, { eventID: eventId });
            else
                w.fbq?.('track', name, params);
            break;
        case 'tiktok': {
            // Use ttq.instance(pixelId) to target each pixel individually,
            // since the global ttq.track() only fires for the sdkid pixel.
            const pixelId = pixel && 'pixelId' in pixel ? pixel.pixelId : null;
            const target = pixelId && w.ttq?.instance ? w.ttq.instance(pixelId) : w.ttq;
            if (name === 'Pageview') {
                target?.page?.();
            }
            else if (eventId) {
                target?.track?.(name, params, { event_id: eventId });
            }
            else {
                target?.track?.(name, params);
            }
            break;
        }
        case 'snapchat':
            w.snaptr?.('track', name, params);
            break;
        case 'pinterest':
            // Pinterest handles page views via pintrk('page'), not pintrk('track', 'pagevisit')
            if (name === 'pagevisit') {
                w.pintrk?.('page');
            }
            else {
                w.pintrk?.('track', name, params);
            }
            break;
        case 'gtm':
            fireGTM(name, params);
            break;
    }
}
function fireGTM(event, params) {
    try {
        const w = window;
        if (w.gtag) {
            w.gtag('event', event, params);
        }
        else if (w.dataLayer) {
            w.dataLayer.push({ event, ...params });
        }
    }
    catch (error) {
        console.error('[SDK GTM] Error:', error);
    }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
// ===========================================================================
// Pixel initialization (browser-only)
// All pixel globals are accessed via `win` typed as `any` to avoid
// `declare global` augmentation issues across tsconfig scopes.
// ===========================================================================
/* eslint-disable @typescript-eslint/no-explicit-any */
const win = (typeof window !== 'undefined' ? window : undefined);
const SCRIPT_LOAD_TIMEOUT_MS = 5000;
function waitForScriptLoad(script) {
    // If the script has already loaded, resolve immediately
    if (script.dataset.loaded)
        return Promise.resolve();
    return new Promise((resolve) => {
        const done = () => { script.dataset.loaded = '1'; clearTimeout(timer); resolve(); };
        const timer = setTimeout(done, SCRIPT_LOAD_TIMEOUT_MS);
        script.addEventListener('load', done, { once: true });
        script.addEventListener('error', done, { once: true });
    });
}
let _metaScriptEl = null;
function initMetaPixel(pixelId) {
    if (!win)
        return Promise.resolve();
    // Initialize Meta base code once
    if (!win.fbq) {
        const n = function (...args) {
            if (n.callMethod)
                n.callMethod(...args);
            else
                n.queue.push(args);
        };
        n.queue = [];
        n.loaded = true;
        n.version = '2.0';
        win.fbq = n;
        if (!win._fbq)
            win._fbq = n;
        const t = document.createElement('script');
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        const s = document.getElementsByTagName('script')[0];
        s?.parentNode?.insertBefore(t, s);
        _metaScriptEl = t;
    }
    // Register each pixel ID (fbq supports multiple pixels via multiple init calls)
    win.fbq('init', pixelId);
    return _metaScriptEl ? waitForScriptLoad(_metaScriptEl) : Promise.resolve();
}
let _tiktokBaseInitialized = false;
function initTikTokPixel(pixelId) {
    if (!win)
        return Promise.resolve();
    // Initialize TikTok base code once
    if (!_tiktokBaseInitialized) {
        _tiktokBaseInitialized = true;
        win.TiktokAnalyticsObject = 'ttq';
        const ttq = (win.ttq = win.ttq || []);
        ttq.methods = [
            'page', 'track', 'identify', 'instances', 'debug', 'on', 'off',
            'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
            'holdConsent', 'revokeConsent', 'grantConsent',
        ];
        ttq.setAndDefer = function (t, e) {
            t[e] = function (...args) { t.push([e, ...args]); };
        };
        for (const method of ttq.methods) {
            ttq.setAndDefer(ttq, method);
        }
        ttq.instance = function (t) {
            const e = ttq._i[t] || [];
            for (const method of ttq.methods) {
                ttq.setAndDefer(e, method);
            }
            return e;
        };
        ttq.load = function (e, n) {
            const r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
            ttq._i = ttq._i || {};
            ttq._i[e] = [];
            ttq._i[e]._u = r;
            ttq._t = ttq._t || {};
            ttq._t[e] = +new Date();
            ttq._o = ttq._o || {};
            ttq._o[e] = n || {};
            const s = document.createElement('script');
            s.type = 'text/javascript';
            s.async = true;
            s.src = r + '?sdkid=' + e + '&lib=ttq';
            const p = document.getElementsByTagName('script')[0];
            p?.parentNode?.insertBefore(s, p);
        };
    }
    // Skip if this specific pixel ID is already registered
    if (win.ttq._i?.[pixelId])
        return Promise.resolve();
    // Register pixel and load its script
    win.ttq.load(pixelId);
    // Find the script we just created and wait for it
    const scripts = document.querySelectorAll('script[src*="analytics.tiktok.com/i18n/pixel/events.js"]');
    const lastScript = scripts[scripts.length - 1];
    return lastScript ? waitForScriptLoad(lastScript) : Promise.resolve();
}
let _snapchatScriptEl = null;
function initSnapchatPixel(pixelId) {
    if (!win)
        return Promise.resolve();
    // Initialize Snapchat base code once
    if (!win.snaptr) {
        const a = function (...args) {
            if (a.handleRequest)
                a.handleRequest(...args);
            else
                a.queue.push(args);
        };
        a.queue = [];
        win.snaptr = a;
        const r = document.createElement('script');
        r.async = true;
        r.src = 'https://sc-static.net/scevent.min.js';
        const u = document.getElementsByTagName('script')[0];
        u?.parentNode?.insertBefore(r, u);
        _snapchatScriptEl = r;
    }
    // Register each pixel ID (snaptr supports multiple pixels via multiple init calls)
    win.snaptr('init', pixelId);
    return _snapchatScriptEl ? waitForScriptLoad(_snapchatScriptEl) : Promise.resolve();
}
let _pinterestScriptEl = null;
function initPinterestPixel(pixelId) {
    if (!win)
        return Promise.resolve();
    // Initialize Pinterest base code once
    if (!win.pintrk) {
        const a = function (...args) { a.queue.push(args); };
        a.queue = [];
        win.pintrk = a;
        const s = document.createElement('script');
        s.async = true;
        s.src = 'https://s.pinimg.com/ct/core.js';
        const u = document.getElementsByTagName('script')[0];
        u?.parentNode?.insertBefore(s, u);
        _pinterestScriptEl = s;
    }
    // Register each pixel ID (pintrk supports multiple pixels via multiple load calls)
    win.pintrk('load', pixelId);
    // Note: pintrk('page') is NOT called here — the auto page-view fires via fire().
    return _pinterestScriptEl ? waitForScriptLoad(_pinterestScriptEl) : Promise.resolve();
}
function initGTM(containerId) {
    if (!win || !containerId)
        return Promise.resolve();
    const isGtmContainer = containerId.startsWith('GTM-');
    const scriptUrlPart = isGtmContainer
        ? `googletagmanager.com/gtm.js?id=${containerId}`
        : `googletagmanager.com/gtag/js?id=${containerId}`;
    if (document.querySelector(`script[src*="${scriptUrlPart}"]`))
        return Promise.resolve();
    win.dataLayer = win.dataLayer || [];
    // Push referrer domain context into dataLayer before GTM loads, so tags
    // inside the container can use it for cross-domain tracking configuration.
    try {
        const ref = document.referrer && new URL(document.referrer).hostname;
        if (ref && ref !== window.location.hostname) {
            win.dataLayer.push({ tagada_referrer_domain: ref });
        }
    }
    catch { /* ignore invalid referrer */ }
    if (isGtmContainer) {
        win.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        const f = document.getElementsByTagName('script')[0];
        const j = document.createElement('script');
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + containerId;
        if (f?.parentNode)
            f.parentNode.insertBefore(j, f);
        else
            (document.head || document.getElementsByTagName('head')[0])?.appendChild(j);
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        (document.body || document.getElementsByTagName('body')[0])?.insertBefore(noscript, document.body?.firstChild ?? null);
        return waitForScriptLoad(j);
    }
    else {
        if (!win.gtag) {
            win.gtag = function (..._args) { win.dataLayer.push(arguments); };
        }
        win.gtag('js', new Date());
        // Enable cross-domain tracking: accept incoming _gl parameter from referring
        // domains (e.g. Shopify store → TagadaPay checkout) so Google preserves the
        // client-id/session across the redirect.
        const linkerConfig = { accept_incoming: true };
        try {
            const ref = document.referrer && new URL(document.referrer).hostname;
            if (ref && ref !== window.location.hostname) {
                linkerConfig.domains = [ref, window.location.hostname];
            }
        }
        catch { /* ignore invalid referrer */ }
        win.gtag('config', containerId, { linker: linkerConfig });
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + containerId;
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode)
            firstScript.parentNode.insertBefore(script, firstScript);
        else
            (document.head || document.getElementsByTagName('head')[0])?.appendChild(script);
        return waitForScriptLoad(script);
    }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
// ---------------------------------------------------------------------------
// Tracker factory
// ---------------------------------------------------------------------------
/**
 * Create a pixel tracker for a given pixels config.
 * Kicks off pixel script init immediately, fires PageView once init resolves,
 * and returns a `track()` function that no-ops until init completes.
 */
export function createPixelTracker(pixels) {
    const shouldTrackEvent = createDuplicateGuard(5000);
    let pixelsInitialized = false;
    const initPromise = (async () => {
        if (!pixels || typeof window === 'undefined')
            return;
        const loadPromises = [];
        try {
            pixels.facebook?.forEach((px) => { if (px.enabled && px.pixelId)
                loadPromises.push(initMetaPixel(px.pixelId)); });
            pixels.tiktok?.forEach((px) => { if (px.enabled && px.pixelId)
                loadPromises.push(initTikTokPixel(px.pixelId)); });
            pixels.snapchat?.forEach((px) => { if (px.enabled && px.pixelId)
                loadPromises.push(initSnapchatPixel(px.pixelId)); });
            pixels.pinterest?.forEach((px) => { if (px.enabled && px.pixelId)
                loadPromises.push(initPinterestPixel(px.pixelId)); });
            pixels.gtm?.forEach((px) => { if (px.enabled && px.containerId)
                loadPromises.push(initGTM(px.containerId)); });
            await Promise.all(loadPromises);
        }
        catch (error) {
            console.error('[SDK Pixels] Initialization error:', error);
        }
        pixelsInitialized = true;
    })();
    const track = (eventName, parameters = {}, options = {}) => {
        if (!pixels || !pixelsInitialized)
            return;
        if (!shouldTrackEvent(eventName, parameters, options.eventId))
            return;
        try {
            const events = resolvePixelEvents(pixels, eventName, parameters);
            // Deduplicate by provider: Meta/Snapchat/Pinterest SDKs broadcast
            // to all registered pixel IDs internally, so we only need to fire once per
            // provider. GTM and TikTok need per-pixel firing (GTM for Google Ads
            // send_to targeting, TikTok because ttq.instance() targets a specific pixel).
            const firedProviders = new Set();
            for (const { provider, mapped, pixel } of events) {
                if (provider === 'gtm' || provider === 'tiktok') {
                    fire(provider, mapped.name, mapped.params, pixel, options);
                }
                else {
                    if (!firedProviders.has(provider)) {
                        fire(provider, mapped.name, mapped.params, undefined, options);
                        firedProviders.add(provider);
                    }
                }
            }
        }
        catch (error) {
            console.error('[SDK Pixels] Tracking error:', error);
        }
    };
    // Auto page-view: fire once after init resolves.
    initPromise.then(() => {
        if (typeof window === 'undefined')
            return;
        track('PageView', { path: window.location.pathname });
    });
    return {
        initPromise,
        get pixelsInitialized() { return pixelsInitialized; },
        track,
    };
}
function readPixelsFromStepConfig() {
    if (typeof window === 'undefined')
        return undefined;
    const raw = window.__TGD_STEP_CONFIG__;
    if (!raw)
        return undefined;
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const pixels = parsed?.pixels;
        return pixels && typeof pixels === 'object' ? pixels : undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * One-shot bootstrap: reads `window.__TGD_STEP_CONFIG__.pixels`, creates a
 * tracker, kicks off init, and stashes it on `window.__TGD_PIXEL_TRACKER__`.
 *
 * Idempotent — both the islands SDK and the full-SPA runtime may call this,
 * but only the first call creates a tracker. Returns the active tracker
 * (existing or newly created), or null if there's no window or no pixels.
 */
export function bootstrapPixelTrackerFromWindow() {
    if (typeof window === 'undefined')
        return null;
    if (window.__TGD_PIXEL_TRACKER__)
        return window.__TGD_PIXEL_TRACKER__;
    const pixels = readPixelsFromStepConfig();
    if (!pixels)
        return null;
    const tracker = createPixelTracker(pixels);
    window.__TGD_PIXEL_TRACKER__ = tracker;
    return tracker;
}
